import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveMetaDeliveryMutation,
  SaveMetaDelivery_Input,
  useMetaDeliveryFormQuery
} from 'generated/graphql';

export type IFormState = {
  id: string;
  included: boolean;
  notesForClient: string;
};

const initialState: IFormState = {
  id: '',
  included: false,
  notesForClient: ''
};

export function useFormState(id: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'Id',
        type: 'text'
      },
      included: {
        label: 'Included',
        type: 'text'
      },
      notesForClient: {
        label: 'Notes',
        type: 'text'
      }
    }
  });

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (formState.id === '' && data.id !== '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
    return () => {};
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveMetaDelivery() {
  const [
    fnMutate,
    tuple
  ] = useSaveMetaDeliveryMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveMetaDelivery_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export interface IData extends IFormState {}

export function useQuery(id: string) {
  const tuple = useMetaDeliveryFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.metaDelivery?.id ?? '',
        included: tuple.data?.metaDelivery?.included ?? false,
        notesForClient: tuple.data?.metaDelivery?.notesForClient ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
